import React, { useRef } from 'react';
import { cx } from '../../lib/cx';

/**
 * Single-choice control rendered as a row of buttons (ARIA radiogroup with
 * roving focus: arrow keys move between options).
 * options: [{ value, label, icon?, disabled?, title? }]
 */
export const Segmented = ({ value, onChange, options, label, size = 'md', className, stretch = false }) => {
  const refs = useRef([]);
  const enabled = options.map((o, i) => (!o.disabled ? i : null)).filter((i) => i !== null);

  const move = (index, delta) => {
    const pos = enabled.indexOf(index);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    if (next === undefined) return;
    refs.current[next]?.focus();
    onChange(options[next].value);
  };

  const onKeyDown = (e, index) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); move(index, 1); }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); move(index, -1); }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx(
        'inline-flex rounded-lg border border-line-strong bg-sunken p-0.5',
        stretch && 'flex w-full',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            ref={(el) => { refs.current[index] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (!options.some((o) => o.value === value) && index === 0) ? 0 : -1}
            disabled={option.disabled}
            title={option.title}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cx(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-45',
              size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-sm',
              stretch && 'flex-1',
              selected
                ? 'bg-surface text-fg shadow-sm ring-1 ring-line-strong'
                : 'text-muted hover:text-fg',
            )}
          >
            {Icon && <Icon size={size === 'sm' ? 13 : 14} aria-hidden="true" />}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};
